const capabilities = [
  {
    icon: 'ri-flask-line',
    title: 'Hazardous Material Removal',
    desc: 'Licensed identification, containment, and EPA-regulated disposal of commercial and industrial hazardous substances.',
  },
  {
    icon: 'ri-building-line',
    title: 'Asbestos Abatement',
    desc: 'Certified inspection, testing, and complete asbestos removal in full compliance with all Colorado and federal regulations.',
  },
  {
    icon: 'ri-drop-line',
    title: 'Mold Remediation',
    desc: 'Air quality testing, complete containment, and certified mold removal restoring safe environments in commercial and industrial properties.',
  },
  {
    icon: 'ri-alert-line',
    title: 'Disaster Restoration',
    desc: 'Rapid 24/7 emergency response for flood, fire, and contamination events — immediate containment through full site restoration.',
  },
  {
    icon: 'ri-paint-line',
    title: 'Lead-Based Paint Removal',
    desc: 'Professional lead assessment and certified removal protecting occupant health in compliance with EPA and HUD guidelines.',
  },
  {
    icon: 'ri-file-text-line',
    title: 'Environmental Consulting',
    desc: 'Expert regulatory guidance, Phase I & II assessments, and compliance documentation for every project type.',
  },
];

export default function AboutMissionSection() {
  return (
    <>
      {/* Mission + stats */}
      <section className="bg-white py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start mb-24">

            {/* Left — text */}
            <div className="lg:col-span-5">
              <p className="text-ecos-green text-xs uppercase tracking-widest font-semibold mb-4">Who We Are</p>
              <h2 className="font-serif text-ecos-charcoal text-4xl font-semibold leading-tight mb-6">
                Colorado&apos;s Premier Environmental Remediation Authority
              </h2>
              <div className="w-10 h-0.5 bg-ecos-green mb-8"></div>
              <p className="text-ecos-text text-base leading-relaxed mb-5">
                ECOS Environmental & Disaster Restoration was founded with a singular mission: to deliver environmental remediation services with the precision, documentation, and regulatory rigor that commercial, industrial, and government clients demand.
              </p>
              <p className="text-ecos-text text-base leading-relaxed">
                Over 25 years of operation, we&apos;ve built a team of certified specialists and a proven process that consistently delivers compliant results. We don&apos;t take shortcuts. Every project receives full regulatory analysis, certified execution, and complete documentation.
              </p>
            </div>

            {/* Right — image with stats overlay */}
            <div className="lg:col-span-7 relative">
              <div className="w-full rounded-sm overflow-hidden" style={{ height: '440px' }}>
                <img
                  src="https://public.readdy.ai/ai/img_res/9c3951337fc257ef9e14493b713cad45.jpg"
                  alt="ECOS Environmental professional team reviewing compliance documentation Colorado"
                  title="ECOS Environmental & Disaster Restoration — Certified Environmental Specialists"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Stats overlay card */}
              <div className="absolute -bottom-8 -left-6 bg-ecos-charcoal px-7 py-6 rounded-sm border border-white/10">
                <p className="font-serif text-ecos-gold text-4xl font-semibold">25+</p>
                <p className="text-white/55 text-xs mt-1">Years of Certified Service</p>
              </div>
              <div className="absolute top-6 -right-4 bg-white border border-ecos-border px-5 py-4 rounded-sm">
                <div className="flex gap-0.5 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-ecos-gold text-xs"></i>
                    </div>
                  ))}
                </div>
                <p className="text-ecos-charcoal font-semibold text-sm">4.9 Rating</p>
                <p className="text-ecos-text text-xs">200+ Verified Reviews</p>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-ecos-border rounded-sm overflow-hidden">
            {[
              { value: '25+', label: 'Years in Operation', icon: 'ri-time-line' },
              { value: '3,000+', label: 'Projects Completed', icon: 'ri-checkbox-circle-line' },
              { value: '100%', label: 'Regulatory Compliance Rate', icon: 'ri-shield-star-line' },
              { value: '50+', label: 'Certified Specialists', icon: 'ri-user-star-line' },
            ].map((stat, i) => (
              <div
                key={stat.value}
                className={`flex flex-col items-center text-center py-10 px-6 ${i < 3 ? 'border-b lg:border-b-0 lg:border-r border-ecos-border' : ''} ${i === 1 ? 'border-r border-ecos-border' : ''}`}
              >
                <div className="w-10 h-10 flex items-center justify-center mb-3">
                  <i className={`${stat.icon} text-ecos-green text-2xl`}></i>
                </div>
                <p className="font-serif text-ecos-charcoal text-4xl font-semibold mb-2">{stat.value}</p>
                <p className="text-ecos-text text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities / Services Overview section */}
      <section className="bg-ecos-slate py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14 items-end">
            <div className="lg:col-span-6">
              <p className="text-ecos-green text-xs uppercase tracking-widest font-semibold mb-4">Core Services</p>
              <h2 className="font-serif text-ecos-charcoal text-4xl lg:text-5xl font-semibold leading-tight">
                Full-Spectrum Environmental Remediation Capabilities
              </h2>
            </div>
            <div className="lg:col-span-6 flex items-end">
              <p className="text-ecos-text text-base leading-relaxed">
                From routine compliance consulting to emergency disaster restoration, ECOS Environmental provides every certified environmental service your project may require.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {capabilities.map((cap) => (
              <div key={cap.title} className="group bg-white border border-ecos-border rounded-sm p-7 hover:border-ecos-green transition-all duration-300 cursor-default hover:-translate-y-0.5">
                <div className="w-11 h-11 flex items-center justify-center bg-ecos-green-pale group-hover:bg-ecos-green rounded-sm mb-5 transition-colors">
                  <i className={`${cap.icon} text-ecos-green group-hover:text-white text-xl transition-colors`}></i>
                </div>
                <h3 className="font-serif text-ecos-charcoal text-lg font-semibold mb-3 group-hover:text-ecos-green transition-colors">{cap.title}</h3>
                <p className="text-ecos-text text-sm leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
